import { Sparkles, Sparkle, Stethoscope, ShieldCheck, Smile, Heart, Activity } from "lucide-react";

// Tone palettes derived from the brand colours (cream, bronz auriu, turcoaz, vișiniu, pink, green).
const TONES = {
  cream: { bg: "linear-gradient(135deg, #FFF4DD 0%, #F8E8C9 100%)", icon: "#216C72", accent: "#B8862E" },
  yellow: { bg: "linear-gradient(135deg, #EFD9A8 0%, #B8862E 100%)", icon: "#FFFFFF", accent: "#FFF4DD" },
  green: { bg: "linear-gradient(135deg, #E5F0CF 0%, #C8DEA0 100%)", icon: "#216C72", accent: "#FFFFFF" },
  pink: { bg: "linear-gradient(135deg, #F5D7DF 0%, #D88FA3 100%)", icon: "#FFFFFF", accent: "#FFF4DD" },
  navy: { bg: "linear-gradient(135deg, #2C8B91 0%, #164B50 100%)", icon: "#B8862E", accent: "#FFF4DD" },
};

// Custom tooth glyph (Lucide doesn't ship a tooth icon).
function ToothGlyph({ size = 96, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M22 8c-6 0-10 4-10 10 0 4 2 6 2 12 0 8 2 14 4 20 1 3 3 6 5 6 3 0 3-6 4-12 1-4 2-6 5-6s4 2 5 6c1 6 1 12 4 12 2 0 4-3 5-6 2-6 4-12 4-20 0-6 2-8 2-12 0-6-4-10-10-10-3 0-5 2-10 2s-7-2-10-2z" />
    </svg>
  );
}

const ICONS = {
  sparkles: Sparkles,
  sparkle: Sparkle,
  stethoscope: Stethoscope,
  shield: ShieldCheck,
  smile: Smile,
  heart: Heart,
  activity: Activity,
};

export function ServiceImage({ service, className = "", aspect = "aspect-[16/10]" }) {
  const { image, title, icon = "sparkle", tone = "cream", fit = "cover" } = service;
  if (image) {
    const objectClass = fit === "contain" ? "object-contain" : "object-cover";
    const bg = fit === "contain" ? "bg-[#FFF4DD]" : "bg-brand-cream";
    return (
      <div className={`${aspect} overflow-hidden ${bg} ${className}`}>
        <img
          src={image}
          alt={title}
          className={`w-full h-full ${objectClass} transition-transform duration-700 group-hover:scale-[1.05]`}
          draggable={false}
          loading="lazy"
        />
      </div>
    );
  }

  const palette = TONES[tone] || TONES.cream;
  const Icon = ICONS[icon] || Sparkle;

  return (
    <div
      className={`${aspect} relative overflow-hidden ${className}`}
      style={{ background: palette.bg }}
      aria-label={title}
    >
      {/* Decorative tooth silhouette in the corner */}
      <div className="absolute -right-6 -bottom-8 opacity-25 transition-transform duration-700 group-hover:scale-110" style={{ color: palette.accent }}>
        <ToothGlyph size={180} color="currentColor" />
      </div>
      {/* Tiny floating accent dots */}
      <div className="absolute top-5 right-6 w-1.5 h-1.5 rounded-full opacity-70" style={{ background: palette.accent }} />
      <div className="absolute top-9 right-12 w-2 h-2 rounded-full opacity-50" style={{ background: palette.accent }} />
      <div className="absolute bottom-8 left-7 w-1 h-1 rounded-full opacity-70" style={{ background: palette.accent }} />

      {/* Centered service icon */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          className="w-20 h-20 rounded-2xl flex items-center justify-center shadow-lg backdrop-blur-sm transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3"
          style={{ background: "rgba(255,255,255,0.85)" }}
        >
          {icon === "tooth" ? (
            <ToothGlyph size={44} color={palette.icon} />
          ) : (
            <Icon className="w-10 h-10" style={{ color: palette.icon }} strokeWidth={1.6} />
          )}
        </div>
      </div>
    </div>
  );
}
